import { Box, Grid2 as Grid, Input, Link, Slider, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { LogoError } from "../../../../redux/slices/documentTemplate";
import { Attachment } from "../../../../redux/slices/material";
import { CollapsableHeader, ImageUploadList, TextField } from "../../../../components";

export const CoverPageImageUpload: React.FC<{
    title: string;
    hasReleased?: boolean;
    showSwitch?: boolean;
    switchStatus?: boolean;
    onSwitchChanged?: (isChecked: boolean) => void;

    image: (File | Attachment)[] | undefined;
    onImageUpload: (file: File) => void;
    onDelete: (file: File | Attachment) => void;

    btnName: string;

    horizontalPosition: number;
    onHorizontalPositionChange: (position: number) => void;

    verticalPosition: number;
    onVerticalPositionChange: (position: number) => void;

    height: number;
    width: number;
    onHeightChange: (height: number) => void;
    onWidthChange: (Width: number) => void;

    error: LogoError;
    children?: JSX.Element;
}> = (props) => {
    const { error } = props;

    const theme = useTheme();

    const isMedium = useMediaQuery("(max-width: 900px)");

    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    const handleChange = (inputValue: string, position: 'x' | 'y') => {

        // Regular expression to allow only up to two digits after the decimal point
        const regex = /^\d*\.?\d{0,2}$/;

        if (regex.test(inputValue)) {
            if (position === 'x') {
                props.onHorizontalPositionChange(inputValue === '' ? 0 : parseFloat(inputValue));
            } else {
                props.onVerticalPositionChange(inputValue === '' ? 0 : parseFloat(inputValue));
            }
        }
    };

    return (
        (<CollapsableHeader
            title={props.title}
            showIcon={true}
            isExpanded={true}
            showSwitch={props.showSwitch}
            switchStatus={props.switchStatus}
            onSwitchChanged={props.onSwitchChanged}
        >
            <>
                <Box sx={{ p: 1 }}>
                    <ImageUploadList
                        hasReleased={props.hasReleased}
                        title={props.btnName}
                        files={Array.isArray(props?.image) ? props?.image : props?.image ? [props?.image] : []}
                        maxFiles={1}
                        onDelete={props.onDelete}
                        onUpload={props.onImageUpload}
                        disableCollapse
                    />

                    <Stack mt={2} spacing={2}>
                        <Grid container spacing={1} columns={12} sx={{ justifyContent: "space-between" }}>

                            <Grid size={{ xs: 12, sm: 12, md: 5, xl: 4 }}>
                                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                                    <TextField
                                        size="small"
                                        label="Height"
                                        type="number"
                                        fullWidth
                                        value={props.hasReleased ? `${props.height}px` : props.height}
                                        onChange={(e) => props.onHeightChange(Number(e.target.value))}
                                        error={error.heightError}
                                        helperText={
                                            error.heightError ? "Please enter the logo height" : ""
                                        }
                                        viewOnly={props.hasReleased}
                                    />
                                    {!props.hasReleased && <Typography variant="body2">px</Typography>}
                                </Stack>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
                                    <Tooltip title="Aspect ratio locked">
                                        <Link sx={isMedium ? { transform: "rotate(90deg)" } : {}} />
                                    </Tooltip>
                                </Stack>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 12, md: 5, xl: 4 }}>
                                <Stack direction="row" sx={{
                                    alignItems: "center",
                                    justifyContent: props.hasReleased && !isSmall ? "flex-end" : "normal",
                                    gap: 1
                                }}>
                                    <TextField
                                        size="small"
                                        label="Width"
                                        type="number"
                                        fullWidth
                                        value={props.hasReleased ? `${props.width}px` : props.width}
                                        onChange={(e) => props.onWidthChange(Number(e.target.value))}
                                        error={error.widthError}
                                        helperText={error.widthError ? "Please enter the logo width" : ""}
                                        viewOnly={props.hasReleased}
                                    />
                                    {!props.hasReleased && <Typography variant="body2">px</Typography>}
                                </Stack>
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} columns={12} sx={{ justifyContent: "space-between" }}>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2">Horizontal Position:</Typography>
                                <Box sx={{ px: 1, display: "flex", gap: 1, alignItems: "center" }}>
                                    <Slider disabled={props.hasReleased} value={+props.horizontalPosition} onChange={(e, value) => handleChange(`${value}`, 'x')} valueLabelDisplay="auto" />
                                    <Input
                                        value={+props.horizontalPosition}
                                        size="small"
                                        disabled={props.hasReleased}
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                        onChange={(e) => handleChange(`${e.target.value}`, 'x')}
                                        inputProps={{
                                            min: 0,
                                            max: 100,
                                            type: 'number',
                                        }}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2">Vertical Position:</Typography>
                                <Box sx={{ px: 1, display: "flex", gap: 1, alignItems: "center" }}>
                                    <Slider disabled={props.hasReleased} value={+props.verticalPosition} onChange={(e, value) => handleChange(`${value}`, 'y')} valueLabelDisplay="auto" />
                                    <Input
                                        value={+props.verticalPosition}
                                        size="small"
                                        disabled={props.hasReleased}
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                        onChange={(e) => handleChange(`${e.target.value}`, 'y')}
                                        inputProps={{
                                            min: 0,
                                            max: 100,
                                            type: 'number',
                                        }}
                                    />
                                </Box>
                            </Grid>

                        </Grid>
                    </Stack>
                </Box>

                {props.children}
            </>
        </CollapsableHeader>)
    );
};
